import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  updateDoc,
} from "firebase/firestore";
import { db, storage } from "./firebaseConfig";
import {
  APPLICANT_PHOTO_MAX_BYTES,
  applicantIdLimit,
  compressApplicantId,
  compressApplicantPhoto,
} from "./utils/uploads";
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
  ref,
  deleteObject,
} from "firebase/storage";

export const getVideos = async () => {
  try {
    const docRef = doc(db, "youtubeLinks", "you-tube-links"); // Replace 'your-document-id' with the actual document ID
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      console.log("Document data:", docSnap.data());
      return docSnap.data();
    } else {
      console.log("No such document!");
    }
  } catch (error) {
    console.error("Error fetching document:", error);
  }
};

export const getCampaings = async () => {
  try {
    const docRef = doc(db, "campaings", "campaings");
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      console.log("Document data:", docSnap.data());
      return docSnap.data();
    } else {
      console.log("No such document!");
    }
  } catch (error) {
    console.error("Error fetching document:", error);
  }
};

export const getResults = async () => {
  try {
    const docRef = doc(db, "results", "resultsPhoto");
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      console.log("Document data:", docSnap.data());
      return docSnap.data();
    } else {
      console.log("No such document!");
    }
  } catch (error) {
    console.error("Error fetching document:", error);
  }
};

// Soft-deleted teachers used to be fetched and then hidden in JSX, which
// shipped removed people's names and photos to every visitor. Dropping them
// here keeps that out of the browser entirely.
//
// This cannot be a `where("isDeleted", "!=", true)` query yet: Firestore
// skips documents that lack the field, and teachers created before soft
// delete existed have no `isDeleted` at all. New records below are written
// with `isDeleted: false`, so once the Phase 3 backfill stamps the old ones
// this can move into the query itself.
const readTeachers = async (collectionName, { includeDeleted = false } = {}) => {
  try {
    const querySnapshot = await getDocs(collection(db, collectionName));
    const teachers = [];

    querySnapshot.forEach((doc) => {
      const teacher = { id: doc.id, ...doc.data() };
      if (includeDeleted || teacher.isDeleted !== true) {
        teachers.push(teacher);
      }
    });
    return teachers;
  } catch (error) {
    console.error("Error fetching documents: ", error);
    throw error;
  }
};

export const getTeachers = async (options) => readTeachers("teachersDetails", options);

export const getSchoolTeachers = async (options) => readTeachers("schoolTeachers", options);

// `regDate` is a date-only string, so every applicant who registers on the
// same day sorts identically and their order is whatever Firestore happens to
// return. `createdAt` is a full ISO timestamp written at submit time, which
// gives a real "most recent first" ordering.
//
// Documents created before `createdAt` existed fall back to `regDate`, which
// still sorts correctly against an ISO timestamp: "2026-09-20" compares less
// than "2026-09-20T09:15:00.000Z", so a legacy record sorts below a new one
// from the same day rather than jumping above it.
const registeredAt = (row) => row.createdAt || row.regDate || "";

export const byNewestFirst = (rows) =>
  [...rows].sort((a, b) => registeredAt(b).localeCompare(registeredAt(a)));

export const getTutors = async () => {
  try {
    // Create a query with ordering by regDate in descending order
    const tutorsQuery = query(collection(db, "pretutors"), orderBy("regDate", "desc"));

    const querySnapshot = await getDocs(tutorsQuery);
    const rows = querySnapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }));

    // The query still orders by `regDate` rather than `createdAt`: Firestore
    // silently drops documents that lack the field being ordered on, and
    // records written before `createdAt` existed would vanish from the panel
    // entirely. The precise ordering is applied here instead.
    return byNewestFirst(rows);
  } catch (error) {
    console.error("Error fetching documents: ", error);
    throw error; // Optionally re-throw the error for better error handling
  }
};

export const getJobPlacements = async () => {
  try {
    // Create a query with ordering by regDate in descending order
    const tutorsQuery = query(collection(db, "preJob"), orderBy("regDate", "desc"));

    const querySnapshot = await getDocs(tutorsQuery);
    const rows = querySnapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }));

    // The query still orders by `regDate` rather than `createdAt`: Firestore
    // silently drops documents that lack the field being ordered on, and
    // records written before `createdAt` existed would vanish from the panel
    // entirely. The precise ordering is applied here instead.
    return byNewestFirst(rows);
  } catch (error) {
    console.error("Error fetching documents: ", error);
    throw error; // Optionally re-throw the error for better error handling
  }
};

// Registration dates used to be a form field the applicant could edit. They
// are stamped here instead, so the record always says when it actually
// arrived.
const registrationStamps = () => {
  const now = new Date();
  return { regDate: now.toISOString().split("T")[0], createdAt: now.toISOString() };
};

// Public media (teachers, results, campaigns) is world-readable, so the
// download URL can be read back after the upload.
const uploadFile = async (file, storagePath) => {
  const fileRef = storageRef(storage, storagePath);
  const metadata = file.type ? { contentType: file.type } : undefined;
  await uploadBytes(fileRef, file, metadata);
  return getDownloadURL(fileRef);
};

// Applicant photos and ID proofs are not publicly readable. getDownloadURL
// asks Storage for that read, and a browser is not allowed to set
// firebaseStorageDownloadTokens itself. The upload response already includes
// the token Storage minted, which is the same link an admin later opens.
const uploadApplicantFile = async (file, storagePath) => {
  const fileRef = storageRef(storage, storagePath);
  const endpoint =
    `https://firebasestorage.googleapis.com/v0/b/${fileRef.bucket}/o` +
    `?name=${encodeURIComponent(fileRef.fullPath)}`;
  const response = await fetch(endpoint, {
    method: "POST",
    headers: file.type ? { "Content-Type": file.type } : undefined,
    body: file,
  });
  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(payload?.error?.message || "Upload failed");
    error.code = response.status === 403 ? "storage/unauthorized" : "storage/unknown";
    throw error;
  }

  const token = String(payload.downloadTokens || "")
    .split(",")
    .filter(Boolean)[0];
  if (!token) {
    const error = new Error("Upload did not return a download token");
    error.code = "storage/unknown";
    throw error;
  }

  const objectPath = encodeURIComponent(payload.name || fileRef.fullPath);
  const bucket = payload.bucket || fileRef.bucket;
  return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${objectPath}?alt=media&token=${token}`;
};

// The number inputs arrive as numbers. The rules only accept text.
const asText = (value) => (value == null ? "" : String(value));

// The form compresses as the applicant picks a file. Doing it again here
// means a multi-megabyte original cannot reach Storage if that step is skipped.
const prepareApplicantUploads = async (photo, idProof) => {
  const compressedPhoto = await compressApplicantPhoto(photo);
  const compressedId = await compressApplicantId(idProof);

  if (!compressedPhoto || compressedPhoto.size > APPLICANT_PHOTO_MAX_BYTES) {
    throw new Error("Photo is too large after compression");
  }

  const idLimit = applicantIdLimit(compressedId);
  if (!compressedId || compressedId.size > idLimit) {
    throw new Error("ID proof is too large");
  }

  return { photo: compressedPhoto, idProof: compressedId };
};

export const submitJobData = async (data) => {
  try {
    const files = await prepareApplicantUploads(data.photo, data.idProof);
    const photoURL = await uploadApplicantFile(
      files.photo,
      "imagesPlacement/" + `${new Date().getTime()}_${files.photo.name}`
    );
    const idProofURL = await uploadApplicantFile(
      files.idProof,
      "idsPlacement/" + `${new Date().getTime()}_${files.idProof.name}`
    );
    const jobData = {
      name: data.name,
      email: data.email,
      referral: data.referral,
      age: asText(data.age),
      gender: data.gender,
      fatherName: data.fatherName,
      mobileNo: data.mobileNo,
      alternateMobileNo: data.alternateMobileNo,
      experience: asText(data.experience),
      currentAddress: data.currentAddress,
      permanentAddress: data.permanentAddress,
      qualification: data.qualification,
      prefferedArea: data.prefferedArea,
      maritalStatus: data.maritalStatus,
      photo: photoURL,
      idProof: idProofURL,
      profile: data.profile,
      salaryExpected: data.salaryExpected,
      ...registrationStamps(),
    };

    const docRef = await addDoc(collection(db, "preJob"), jobData);
    console.log("Document written with ID: ", docRef.id);
  } catch (error) {
    console.error("Error submitting data:", error);
    throw error;
  }
};

export const submitTutorsData = async (data) => {
  try {
    const files = await prepareApplicantUploads(data.photo, data.idProof);
    const photoURL = await uploadApplicantFile(
      files.photo,
      "images/" + `${new Date().getTime()}_${files.photo.name}`
    );
    const idProofURL = await uploadApplicantFile(
      files.idProof,
      "ids/" + `${new Date().getTime()}_${files.idProof.name}`
    );
    const jobData = {
      address: data.address,
      age: asText(data.age),
      alternateMobileNo: data.alternateMobileNo,
      areaVisit: data.areaVisit,
      declaration: data.declaration,
      email: data.email,
      // The form used to bind a RadioGroup to `value={true}` / `value={false}`,
      // which hands back the *strings* "true" and "false" -- both truthy, so
      // every applicant was recorded as speaking English. The form now sends
      // "Yes"/"No" directly.
      engComm: data.engComm === "Yes" || data.engComm === true ? "Yes" : "No",
      experience: asText(data.experience),
      fees: data.fees,
      gender: data.gender,
      idProof: idProofURL,
      maritalStatus: data.maritalStatus,
      mobileNo: data.mobileNo,
      name: data.name,
      photo: photoURL,
      qualification: data.qualification,
      refrence: data.refrence,
      subjects: data.subjects,
      transportMode: data.transportMode,
      ...registrationStamps(),
    };
    const docRef = await addDoc(collection(db, "pretutors"), jobData);
    console.log("Document written with ID: ", docRef.id);
  } catch (error) {
    console.error("Error submitting data:", error);
    throw error;
  }
};

export const submitInstituteTeacher = async (data) => {
  try {
    const photoURL = await uploadFile(
      data.photo,
      "teachers/" + `${new Date().getTime()}_${data.photo.name}`
    );
    const jobData = {
      photo: photoURL,
      name: data.name,
      experience: data.experience,
      qualification: data.qualification,
      subjects: data.subjects,
      isDeleted: false,
    };

    const docRef = await addDoc(collection(db, "teachersDetails"), jobData);
    console.log("Document written with ID: ", docRef.id);
  } catch (error) {
    console.error("Error submitting data:", error);
    throw error;
  }
};

export const editInstituteTeacher = async (teacherId, updatedData) => {
  console.log(teacherId, updatedData);
  try {
    // Get reference to the document
    const teacherRef = doc(db, "teachersDetails", teacherId);

    // Fetch existing document data
    const teacherDoc = await getDoc(teacherRef);
    if (!teacherDoc.exists()) {
      throw new Error("Teacher not found");
    }

    let photoURL = teacherDoc.data().photo; // Keep the existing photo by default

    // If a new photo is provided, upload it
    if (updatedData.photo && updatedData.photo instanceof File) {
      photoURL = await uploadFile(
        updatedData.photo,
        "teachers/" + `${new Date().getTime()}_${updatedData.photo.name}`
      );
    }

    // Prepare updated data
    const updateData = {
      name: updatedData.name,
      experience: updatedData.experience,
      qualification: updatedData.qualification,
      subjects: updatedData.subjects,
      photo: photoURL, // Update only if a new photo is uploaded
    };

    // Update the Firestore document
    await updateDoc(teacherRef, updateData);
    console.log("Document successfully updated");
  } catch (error) {
    console.error("Error updating data:", error);
    throw error;
  }
};

export const softDeleteTeacher = async (teacherId) => {
  try {
    const teacherRef = doc(db, "teachersDetails", teacherId);
    await updateDoc(teacherRef, { isDeleted: true });
    console.log(`Teacher ${teacherId} marked as deleted.`);
  } catch (error) {
    console.error("Error soft deleting teacher:", error);
    throw error;
  }
};

export const submitSchoolTeacher = async (data) => {
  try {
    const photoURL = await uploadFile(
      data.photo,
      "schoolTeachers/" + `${new Date().getTime()}_${data.photo.name}`
    );
    const jobData = {
      photo: photoURL,
      name: data.name,
      experience: data.experience,
      qualification: data.qualification,
      subjects: data.subjects,
      isDeleted: false,
    };

    const docRef = await addDoc(collection(db, "schoolTeachers"), jobData);
    console.log("Document written with ID: ", docRef.id);
  } catch (error) {
    console.error("Error submitting data:", error);
    throw error;
  }
};

export const editSchoolTeacher = async (teacherId, updatedData) => {
  try {
    // Get reference to the document
    const teacherRef = doc(db, "schoolTeachers", teacherId);

    // Fetch existing document data
    const teacherDoc = await getDoc(teacherRef);
    if (!teacherDoc.exists()) {
      throw new Error("Teacher not found");
    }

    let photoURL = teacherDoc.data().photo; // Keep the existing photo by default

    // If a new photo is provided, upload it
    if (updatedData.photo && updatedData.photo instanceof File) {
      photoURL = await uploadFile(
        updatedData.photo,
        "schoolTeachers/" + `${new Date().getTime()}_${updatedData.photo.name}`
      );
    }

    // Prepare updated data
    const updateData = {
      name: updatedData.name,
      experience: updatedData.experience,
      qualification: updatedData.qualification,
      subjects: updatedData.subjects,
      photo: photoURL, // Update only if a new photo is uploaded
    };

    // Update the Firestore document
    await updateDoc(teacherRef, updateData);
    console.log("Document successfully updated");
  } catch (error) {
    console.error("Error updating data:", error);
    throw error;
  }
};

export const softDeleteSchoolTeacher = async (teacherId) => {
  try {
    const teacherRef = doc(db, "schoolTeachers", teacherId);
    await updateDoc(teacherRef, { isDeleted: true });
    console.log(`Teacher ${teacherId} marked as deleted.`);
  } catch (error) {
    console.error("Error soft deleting teacher:", error);
    throw error;
  }
};

export const addResultImage = async (imageFile) => {
  console.log("imageFile", imageFile, imageFile?.photo.name);
  try {
    // Upload image to Firebase Storage
    const imageURL = await uploadFile(
      imageFile.photo,
      `results/${new Date().getTime()}_${imageFile.photo.name}`
    );

    // Get document reference
    const docRef = doc(db, "results", "resultsPhoto");

    // Update Firestore document (add new image URL)
    await updateDoc(docRef, {
      data: arrayUnion(imageURL),
    });

    console.log("Image added successfully:", imageURL);
  } catch (error) {
    console.error("Error adding image:", error);
    throw error;
  }
};

const deleteFile = async (fileURL) => {
  try {
    const fileRef = ref(storage, fileURL);

    await deleteObject(fileRef);
    console.log("File deleted from Storage:", fileURL);
  } catch (error) {
    console.error("Error deleting file from Storage:", error);
  }
};

export const deleteResultImage = async (imageURL) => {
  try {
    const docRef = doc(db, "results", "resultsPhoto");

    // Remove the file from Firebase Storage
    await deleteFile(imageURL);

    // Remove the URL from Firestore
    await updateDoc(docRef, {
      data: arrayRemove(imageURL),
    });

    console.log("Image deleted successfully:", imageURL);
  } catch (error) {
    console.error("Error deleting image:", error);
    throw error;
  }
};

export const addCampaignImage = async (imageFile) => {
  try {
    // Upload image to Firebase Storage
    const imageURL = await uploadFile(
      imageFile.photo,
      `campaings/${new Date().getTime()}_${imageFile.photo.name}`
    );

    // Get document reference
    const docRef = doc(db, "campaings", "campaings");

    // Update Firestore document (add new image URL)
    await updateDoc(docRef, {
      data: arrayUnion(imageURL),
    });

    console.log("Image added successfully:", imageURL);
  } catch (error) {
    console.error("Error adding image:", error);
    throw error;
  }
};

export const deletCampaignImage = async (imageURL) => {
  try {
    const docRef = doc(db, "campaings", "campaings");

    // Remove the file from Firebase Storage
    await deleteFile(imageURL);

    // Remove the URL from Firestore
    await updateDoc(docRef, {
      data: arrayRemove(imageURL),
    });

    console.log("Image deleted successfully:", imageURL);
  } catch (error) {
    console.error("Error deleting image:", error);
    throw error;
  }
};

export const addYoutubeLink = async (url) => {
  try {
    // Get document reference
    const docRef = doc(db, "youtubeLinks", "you-tube-links");
    await updateDoc(docRef, {
      data: arrayUnion(url),
    });

    console.log("Url added successfully:", url);
  } catch (error) {
    console.error("Error adding image:", error);
    throw error;
  }
};

export const deletYoutubeLink = async (url) => {
  try {
    const docRef = doc(db, "youtubeLinks", "you-tube-links");

    // Remove the URL from Firestore
    await updateDoc(docRef, {
      data: arrayRemove(url),
    });

    console.log("Image deleted successfully:", url);
  } catch (error) {
    console.error("Error deleting image:", error);
    throw error;
  }
};

// --- contact enquiries -----------------------------------------------------
// The Contact Us page used to render a <form> that posted nowhere: a visitor
// could fill it in and nothing was ever stored or seen. Enquiries now land in
// a collection the admin panel reads, with the same create-only/admin-read
// shape the application collections use.

export const ENQUIRY_STATUSES = ["new", "in-progress", "closed"];

export const submitEnquiry = async (data) => {
  const enquiry = {
    name: data.name,
    email: data.email,
    mobileNo: data.mobileNo,
    subject: data.subject,
    message: data.message,
    status: "new",
    createdAt: new Date().toISOString(),
  };

  const docRef = await addDoc(collection(db, "enquiries"), enquiry);
  return docRef.id;
};

export const getEnquiries = async () => {
  const enquiriesQuery = query(collection(db, "enquiries"), orderBy("createdAt", "desc"));
  const snapshot = await getDocs(enquiriesQuery);
  return snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }));
};

export const setEnquiryStatus = async (enquiryId, status) => {
  if (!ENQUIRY_STATUSES.includes(status)) {
    throw new Error(`Unknown enquiry status: ${status}`);
  }
  await updateDoc(doc(db, "enquiries", enquiryId), { status });
};

export const deleteEnquiry = async (enquiryId) => {
  await deleteDoc(doc(db, "enquiries", enquiryId));
};
